import { FC, useState } from "react"
import { Listing } from "../../lib/types"
import { Card, CardContent, CardHeader } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import {
  Badge as BadgeIcon,
  ExternalLink,
  MoreVertical,
  TrendingUp,
  Package,
  DollarSign,
  Clock,
  ShoppingCart,
  Undo2,
  ChevronDown,
  ChevronUp
} from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover"
import { useListingContext } from "../../hooks/use-listings"
import { useInventoryContext } from "../../hooks/use-inventory"
import { cn } from "src/lib/utils"
import SubscriptionGate from "../../components/subscription/SubscriptionGate"
import { SubscriptionTypeEnum } from "../../lib/enums"

type EnhancedListingCardProps = {
  listing: Listing
  onClick: () => void
  onRecordSale: () => void
  onEdit: () => void
  onArchive: () => void
  onDelete: () => void
  readOnly?: boolean
}

export const EnhancedListingCard: FC<EnhancedListingCardProps> = ({
  listing,
  onClick,
  onRecordSale,
  onEdit,
  onArchive,
  onDelete,
  readOnly = false
}) => {
  const { getSalesForListing, undoSale } = useListingContext()
  const { inventory } = useInventoryContext()
  const sales = getSalesForListing(listing.id)
  const [showSales, setShowSales] = useState(false)

  const getStatusBadge = () => {
    switch (listing.status) {
      case 'active':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-sumoPrimary text-sumoNavy shadow-yellow">
            Active
          </span>
        )
      case 'sold':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-success500 text-white">
            Sold Out
          </span>
        )
      case 'archived':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-neutral400 text-white">
            Archived
          </span>
        )
    }
  }

  const progressPercent = (listing.quantitySold / listing.quantityListed) * 100
  const price = Number(listing.price) || 0
  const potentialRevenue = price * listing.quantityListed
  const actualRevenue = sales.reduce((sum, sale) => sum + sale.totalAmount, 0)

  return (
    <Card className="group hover:shadow-strong hover:border-sumoPrimary transition-all duration-200 cursor-pointer relative overflow-hidden dark:bg-neutral800 dark:border-neutral700">
      {/* Status indicator stripe */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${listing.status === 'active' ? 'bg-sumoPrimary' :
        listing.status === 'sold' ? 'bg-success500' :
          'bg-neutral400'
        }`} />

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0" >
            <div className="flex items-center gap-2 mb-2">
              {getStatusBadge()}
              {listing.platform && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-neutral100 dark:bg-neutral700 text-neutral700 dark:text-neutral300">
                  {listing.platform}
                </span>
              )}
            </div>
            <h3 className="font-bold text-lg text-neutral900 dark:text-neutral100 group-hover:text-sumoPrimary transition-colors line-clamp-2">
              {listing.title}
            </h3>
          </div>

          {/* Actions Menu */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="hover:bg-neutral100 dark:hover:bg-neutral700 dark:text-neutral100"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical width={18} height={18} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2 dark:bg-neutral800 dark:border-neutral700" align="end">
              <div className="flex flex-col gap-1">
                {listing.status === 'active' && (
                  <SubscriptionGate
                    requiredTier={SubscriptionTypeEnum.Premium}
                    mode="disable"
                    upgradeMessage="Upgrade to Premium to record sales"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="justify-start text-success600 hover:text-success700 hover:bg-success50 w-full"
                      onClick={(e) => {
                        e.stopPropagation()
                        onRecordSale()
                      }}
                      disabled={readOnly}
                    >
                      <ShoppingCart width={16} height={16} className="mr-2" />
                      Record Sale
                    </Button>
                  </SubscriptionGate>
                )}
                <SubscriptionGate
                  requiredTier={SubscriptionTypeEnum.Premium}
                  mode="disable"
                  upgradeMessage="Upgrade to Premium to edit listings"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start w-full"
                    onClick={(e) => {
                      e.stopPropagation()
                      onEdit()
                    }}
                    disabled={readOnly}
                  >
                    Edit Listing
                  </Button>
                </SubscriptionGate>
                <SubscriptionGate
                  requiredTier={SubscriptionTypeEnum.Premium}
                  mode="disable"
                  upgradeMessage="Upgrade to Premium to archive listings"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start w-full"
                    onClick={(e) => {
                      e.stopPropagation()
                      onArchive()
                    }}
                    disabled={readOnly}
                  >
                    {listing.status === 'archived' ? 'Unarchive' : 'Archive'}
                  </Button>
                </SubscriptionGate>
                <SubscriptionGate
                  requiredTier={SubscriptionTypeEnum.Premium}
                  mode="disable"
                  upgradeMessage="Upgrade to Premium to delete listings"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start text-error500 hover:text-error700 hover:bg-error50 w-full"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete()
                    }}
                    disabled={readOnly}
                  >
                    Delete
                  </Button>
                </SubscriptionGate>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </CardHeader>

      <CardContent className="space-y-4" >
        {/* Quantity Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-neutral600 dark:text-neutral400">
              <Package width={16} height={16} />
              Quantity
            </span>
            <span className="font-semibold text-neutral900 dark:text-neutral100">
              <span className={listing.quantitySold > 0 ? 'text-success600' : ''}>{listing.quantitySold}</span>
              {' / '}
              {listing.quantityListed}
              {' '}
              sold
            </span>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-neutral200 dark:bg-neutral700 rounded-full h-2 overflow-hidden">
            <div
              className={listing.quantityAvailable ? "h-full bg-gradient-to-r from-sumoPrimary to-sumoAmber transition-all duration-500"
                : "h-full bg-gradient-to-r from-success500 to-success700 transition-all duration-500"}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {listing.quantityAvailable > 0 && (
            <p className="text-xs text-neutral500 dark:text-neutral400">
              {listing.quantityAvailable} unit{listing.quantityAvailable !== 1 ? 's' : ''} still available
            </p>
          )}
        </div>

        {/* Revenue Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-neutral50 dark:bg-neutral900 rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs text-neutral600 dark:text-neutral400 mb-1">
              <DollarSign width={14} height={14} />
              Price
            </div>
            <p className="text-lg font-bold text-neutral900 dark:text-neutral100">${price.toFixed(2)}</p>
            <p className="text-xs text-neutral500 dark:text-neutral400">per unit</p>
          </div>

          <div className="bg-success50 dark:bg-success900/20 rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs text-success700 mb-1">
              <TrendingUp width={14} height={14} />
              Revenue
            </div>
            <p className="text-lg font-bold text-success700">${actualRevenue.toFixed(2)}</p>
            <p className="text-xs text-success600">of ${potentialRevenue.toFixed(2)}</p>
          </div>
        </div>

        {/* Sales Count & Expandable List */}
        {sales.length > 0 && (
          <div className="pt-2 border-t border-neutral200 dark:border-neutral700 space-y-2">
            <div
              className="flex items-center justify-between text-sm cursor-pointer hover:bg-neutral50 dark:hover:bg-neutral900 -mx-2 px-2 py-1 rounded"
              onClick={(e) => {
                e.stopPropagation()
                setShowSales(!showSales)
              }}
            >
              <span className="text-neutral600 dark:text-neutral400">Sales recorded:</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-neutral900 dark:text-neutral100">{sales.length} transaction{sales.length !== 1 ? 's' : ''}</span>
                {showSales ? <ChevronUp width={16} height={16} /> : <ChevronDown width={16} height={16} />}
              </div>
            </div>

            {showSales && (
              <div className="space-y-2 pl-2">
                {sales.map((sale) => {
                  const inventoryItem = inventory.find(item => item.id === sale.invSourceId)
                  return (
                    <div
                      key={sale.id}
                      className="flex items-center justify-between text-xs bg-neutral50 dark:bg-neutral900 p-2 rounded"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex-1">
                        <div className="font-semibold text-neutral900 dark:text-neutral100">
                          {sale.quantitySold} unit{sale.quantitySold !== 1 ? 's' : ''} @ ${sale.salePrice.toFixed(2)}
                        </div>
                        <div className="text-neutral500 dark:text-neutral400">
                          {sale.createdAt.toDate().toLocaleDateString()} • ${sale.totalAmount.toFixed(2)}
                        </div>
                        {sale.platform && (
                          <div className="text-neutral400 dark:text-neutral500 text-[10px]">{sale.platform}</div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-warning600 hover:text-warning700 hover:bg-warning50 dark:hover:bg-warning900/20"
                        onClick={async (e) => {
                          e.stopPropagation()
                          if (window.confirm(`Undo this sale of ${sale.quantitySold} unit(s)?`)) {
                            if (inventoryItem) {
                              await undoSale(sale, inventoryItem)
                            }
                          }
                        }}
                      >
                        <Undo2 width={14} height={14} />
                      </Button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* External Link */}
        {listing.externalSourceUrl && (
          <a
            href={listing.externalSourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 text-sm text-blue600 hover:text-blue700 hover:underline"
          >
            <ExternalLink width={14} height={14} />
            View external listing
          </a>
        )}

        {/* Timestamps */}
        <div className="flex items-center gap-4 text-xs text-neutral500 dark:text-neutral400 pt-2 border-t border-neutral200 dark:border-neutral700">
          <div className="flex items-center gap-1">
            <Clock width={12} height={12} />
            Listed {listing.createdAt.toDate().toLocaleDateString()}
          </div>
          {listing.soldAt && (
            <div className="flex items-center gap-1 text-success600">
              <BadgeIcon width={12} height={12} />
              Sold {listing.soldAt.toDate().toLocaleDateString()}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
